import { useState } from "react";
import axios from "axios";
import { useAppStore } from "../store/store";
import initialStore from "../store/initialStore";
import { useNavigate } from "react-router-dom";

const REFRESH_HEADERS = {
  "X-Requested-With": "signmeup",
};

// Hits /auth/refresh with the HttpOnly refresh cookie. On success the store's
// access token is updated and we return it. On failure, returns null.
async function tryRefresh() {
  try {
    const response = await axios.post(
      window.API_ENDPOINT + "/api/v1/auth/refresh",
      null,
      {
        withCredentials: true,
        headers: REFRESH_HEADERS,
      },
    );
    const accessToken = response.data?.data?.accessToken;
    if (accessToken) {
      useAppStore.setState({ token: accessToken });
      return accessToken;
    }
  } catch (err) {
    // Refresh failed — caller decides whether to redirect.
  }
  return null;
}

const useGetApi = () => {
  const { setUserWithForm } = useAppStore();
  let navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const APIENDPOINT = window.API_ENDPOINT;

  const buildAuthedConfig = () => ({
    headers: {
      Authorization: "Bearer " + useAppStore.getState().token,
    },
  });

  // Wraps an authenticated axios call so a 401 transparently refreshes and
  // retries once. If refresh also fails we wipe the session and surface the
  // original error so the caller redirects to /login.
  const authedRequest = async (axiosCall) => {
    try {
      return await axiosCall(buildAuthedConfig());
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshed = await tryRefresh();
        if (refreshed) {
          return await axiosCall(buildAuthedConfig());
        }
        useAppStore.getState().clearSession();
      }
      throw err;
    }
  };

  // Pulls /me + portfolios + outside-asset types and stuffs them into the
  // store as setUserWithForm expects. Used both after a fresh redeem and
  // after a refresh-on-app-load succeeds.
  // preserveForm=true keeps the persisted form state (page-refresh case);
  // otherwise it resets to initial (fresh-login case).
  // If preserveForm=true but the persisted user's id doesn't match /me,
  // a different user is bootstrapping in this browser — wipe the prior
  // form so screen 2.9's recommended-portfolio call doesn't run against
  // the previous user's answers.
  const loadCurrentUser = async ({ preserveForm = false } = {}) => {
    const meResp = await authedRequest((cfg) =>
      axios.get(APIENDPOINT + "/api/v1/Users/me", cfg),
    );
    const me = meResp.data?.data || {};

    const persistedUserId = useAppStore.getState().user?.userId;
    if (
      preserveForm &&
      persistedUserId &&
      me.id &&
      persistedUserId !== me.id
    ) {
      console.warn(
        "Bootstrap user mismatch — resetting form for new user:",
        { persistedUserId, currentUserId: me.id },
      );
      useAppStore.getState().resetFormForNewUser();
      preserveForm = false;
    }

    let portfolios = [];
    let assets = [];
    try {
      const r = await authedRequest((cfg) =>
        axios.get(APIENDPOINT + "/api/v1/Portfolios/get-all", cfg),
      );
      portfolios = r.data?.data || [];
    } catch (e) {
      console.error("Error fetching portfolios:", e);
    }
    try {
      const r = await authedRequest((cfg) =>
        axios.get(APIENDPOINT + "/api/v1/OutsideAssetTypes/get-all", cfg),
      );
      assets = r.data?.data || [];
    } catch (e) {
      console.error("Error fetching assets:", e);
    }

    const portfoliosKey = {};
    portfolios.forEach((portfolio) => {
      portfoliosKey[`portfolio${portfolio.id}`] = portfolio.name;
    });

    const userData = {
      userId: me.id,
      portfolioId: me.artesysPortfolioId || 0,
      artesysPortfolioCode: me.artesysPortfolioId,
      recommendedPortfolioId: me.recommendedPortfolioId,
      firstName: me.firstName,
      lastName: me.lastName,
      emailAddress: me.emailAddress,
      currentAge: me.currentAge,
      accountAssets: me.accountAssets,
      accountValue: me.accountAssets,
      quarterlyFeePercent: me.quarterlyFeePercent,
    };

    const formState = preserveForm
      ? useAppStore.getState().form || initialStore.form
      : initialStore.form;

    const userWithForm = {
      user: userData,
      token: useAppStore.getState().token,
      form: formState,
      formOptions: {
        portfolios,
        portfoliosKey,
        assets,
      },
    };

    setUserWithForm(userWithForm);
    return userWithForm;
  };

  // Submits the 5-field PII to /auth/login. The server only sends the magic
  // link if all 5 match the stored user; either way it returns the same
  // generic response so the caller can't probe for valid users.
  const callLogin = async (
    { firstName, lastName, dob, lastFourSocial, email },
    captchaToken = null,
  ) => {
    try {
      setLoading(true);

      const postData = {
        firstName,
        lastName,
        dateOfBirth: dob,
        last4SS: lastFourSocial,
        emailAddress: email,
      };
      if (captchaToken) {
        postData.captchaToken = captchaToken;
      }

      await axios.post(APIENDPOINT + "/api/v1/auth/login", postData);

      setLoading(false);
      // Always claim "link sent" — server-side decides whether one actually went out.
      return { success: true, magicLinkSent: true, email };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return null;
    }
  };

  // Tries the refresh cookie on app load. On success, populates user state
  // while preserving any in-progress form data restored from localStorage.
  // On failure the session is gone — wipe local state so the next user
  // doesn't inherit it.
  const bootstrapSession = async () => {
    const accessToken = await tryRefresh();
    if (!accessToken) {
      useAppStore.getState().clearSession();
      return false;
    }
    try {
      await loadCurrentUser({ preserveForm: true });
      return true;
    } catch (err) {
      console.error("Bootstrap failed after refresh:", err);
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post(APIENDPOINT + "/api/v1/auth/logout", null, {
        withCredentials: true,
        headers: REFRESH_HEADERS,
      });
    } catch (err) {
      console.error("Logout failed:", err);
    }
    useAppStore.getState().clearSession();
  };

  const callFormUpdate = async (form) => {
    try {
      const portfolioId = form.selectedPortfolioId;
      if (portfolioId && !isNaN(Number(portfolioId))) {
        try {
          const maximizeBenefitsRtq = form.questions.find(
            (q) => q.questionId === "3.16",
          );
          const maximizeBenefitsGoals = form.questions.find(
            (q) => q.questionId === "2.14",
          );
          const maximizeBenefits = maximizeBenefitsRtq || maximizeBenefitsGoals;

          const payload = {
            selectedPortfolioId: Number(portfolioId),
            maximizeBenefits: maximizeBenefits
              ? maximizeBenefits.answerKey
              : null,
            formAnswers: form.questions,
          };

          await authedRequest((cfg) =>
            axios.post(APIENDPOINT + "/api/v1/Users/complete", payload, cfg),
          );
        } catch (portfolioError) {
          console.error(
            "Error updating portfolio but continuing:",
            portfolioError,
          );
        }
      }

      return true;
    } catch (err) {
      console.error("Error in callFormUpdate:", err);
      return true;
    }
  };

  const getRecommendedPortfolioGb = async () => {
    try {
      setLoading(true);
      setError(null);
      const { form } = useAppStore.getState();
      const safeNumber = (value, defaultValue = 0) => {
        const num = Number(value);
        return !isNaN(num) ? num : defaultValue;
      };

      const outsideAssetsQuestion = form.questions.find(
        (q) => q.questionId === "outside-assets",
      );
      let outsideAssets = 0;
      if (
        outsideAssetsQuestion?.answerKey?.outsideAssets &&
        Array.isArray(outsideAssetsQuestion.answerKey.outsideAssets)
      ) {
        outsideAssets = outsideAssetsQuestion.answerKey.outsideAssets.reduce(
          (total, asset) => total + (Number(asset.value) || 0),
          0,
        );
      }
      const retirementAgeQuestion = form.questions.find(
        (q) => q.questionId === "2.3",
      );
      const desiredRetirementAge = retirementAgeQuestion?.answerKey || 65;
      const salaryQuestion = form.questions.find((q) => q.questionId === "2.4");
      const annualSalary = safeNumber(salaryQuestion?.answerKey, 50000);
      const contributionsQuestion = form.questions.find(
        (q) => q.questionId === "2.5",
      );

      let unit = "dollar";
      if (contributionsQuestion?.answerKey?.unit) {
        const formUnit = contributionsQuestion.answerKey.unit;
        if (formUnit === "personalIntervalDollars") unit = "dollar";
        else if (formUnit === "personalIntervalPercentage") unit = "percent";
        else if (formUnit === "dollar" || formUnit === "percent")
          unit = formUnit;
      }

      const gbContributions = {
        unit,
        goalsPersonalContribution: safeNumber(
          contributionsQuestion?.answerKey?.goalsPersonalContribution,
        ),
        goalsEmployerContribution: safeNumber(
          contributionsQuestion?.answerKey?.goalsEmployerContribution,
        ),
        goalsFrequency:
          contributionsQuestion?.answerKey?.goalsFrequency || "monthly",
      };

      const retirementGoalQuestion = form.questions.find(
        (q) => q.questionId === "2.6",
      );
      const retirementGoalBalance = safeNumber(
        retirementGoalQuestion?.answerKey,
        1000000,
      );

      const requestBody = {
        gbOutsideAssets: outsideAssets,
        gbDesiredRetirementAge: safeNumber(desiredRetirementAge, 65),
        gbAnnualSalary: annualSalary,
        gbContributions,
        gbRetirementGoalBalance: retirementGoalBalance,
      };

      const response = await authedRequest((cfg) =>
        axios.post(
          APIENDPOINT + "/api/v1/Portfolios/get-recommended/gb",
          requestBody,
          cfg,
        ),
      );

      return response.data.data;
    } catch (err) {
      console.error("Error getting GB portfolio recommendation:", err);
      setError(err.message);
      navigate("/login?unauthorized=true");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getRecommendedPortfolioRtq = async () => {
    try {
      setLoading(true);
      const { form } = useAppStore.getState();

      const outsideAssetsQuestion = form.questions.find(
        (q) => q.questionId === "outside-assets",
      );
      let outsideAssets = 0;
      if (
        outsideAssetsQuestion?.answerKey?.outsideAssets &&
        Array.isArray(outsideAssetsQuestion.answerKey.outsideAssets)
      ) {
        outsideAssets = outsideAssetsQuestion.answerKey.outsideAssets.reduce(
          (total, asset) => total + (Number(asset.value) || 0),
          0,
        );
      }

      const retirementAgeQuestion = form.questions.find(
        (q) => q.questionId === "3.3",
      );
      const desiredRetirementAge = retirementAgeQuestion?.answerKey || 65;
      const withdrawlTimingQuestion = form.questions.find(
        (q) => q.questionId === "3.4",
      );
      const unexpectedExpensesQuestion = form.questions.find(
        (q) => q.questionId === "3.5",
      );
      const stabilityStockMarketQuestion = form.questions.find(
        (q) => q.questionId === "3.6",
      );
      const avoidMarketLossesQuestion = form.questions.find(
        (q) => q.questionId === "3.7",
      );
      const investmentBalanceAwarenessQuestion = form.questions.find(
        (q) => q.questionId === "3.8",
      );
      const investmentRiskQuestion = form.questions.find(
        (q) => q.questionId === "3.9",
      );
      const safetyRiskValueQuestion = form.questions.find(
        (q) => q.questionId === "3.10",
      );
      const investingConceptsQuestion = form.questions.find(
        (q) => q.questionId === "3.11",
      );
      const investmentTermsQuestion = form.questions.find(
        (q) => q.questionId === "3.12",
      );
      const marketDropQuestion = form.questions.find(
        (q) => q.questionId === "3.13",
      );

      const requestBody = {
        rtqOutsideAssets: parseInt(outsideAssets),
        rtqDesiredRetirementAge: parseInt(desiredRetirementAge),
        rtqWithdrawlTiming: withdrawlTimingQuestion?.answerKey || "1-5",
        rtqUnexpectedExpenses: unexpectedExpensesQuestion?.answerKey || "No",
        rtqStabilityStockMarket:
          stabilityStockMarketQuestion?.answerKey || "Agree",
        rtqAvoidMarketLosses: avoidMarketLossesQuestion?.answerKey || "Agree",
        rtqInvestmentBalanceAwareness:
          investmentBalanceAwarenessQuestion?.answerKey || "Monthly",
        rtqInvestmentRisk: investmentRiskQuestion?.answerKey || "Conservative",
        rtqSafetyRiskValue: safetyRiskValueQuestion?.answerKey || "Safety",
        rtqInvestingConcepts: investingConceptsQuestion?.answerKey || "None",
        rtqInvestmentTerms: investmentTermsQuestion?.answerKey || "None",
        rtqMarketDrop: marketDropQuestion?.answerKey || "1",
      };

      const response = await authedRequest((cfg) =>
        axios.post(
          APIENDPOINT + "/api/v1/Portfolios/get-recommended/rtq",
          requestBody,
          cfg,
        ),
      );
      return response.data.data;
    } catch (err) {
      console.error("Error getting RTQ portfolio recommendation:", err);
      setError(err.message);
      navigate("/login?unauthorized=true");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    callLogin,
    callFormUpdate,
    bootstrapSession,
    logout,
    loading,
    error,
    getRecommendedPortfolioGb,
    getRecommendedPortfolioRtq,
  };
};

export default useGetApi;
